import { MenuItem, Flex, Tag, Text, MenuDivider } from '@chakra-ui/react';
import { RowSelectionState } from '@tanstack/react-table';
import {
  SampleTransportRequest,
  SampleState,
} from '@texas/api/endpoints/samplesApi';
import { ensureEnumNumber } from '@texas/utils/helpers/enumHelpers';
import { useState, useMemo, useCallback } from 'react';

import { defaultIconSize, Icons } from '../Icons';
import { useValueDisclosure } from '@texas/hooks/useValueDisclosure';
import { evaluateSampleNextAction } from './evaluateSampleNextAction';
import {
  GeneralSamplesMenuActions,
  SampleNextActionProps,
  useGeneralSampleModals,
} from './SampleNextAction';
import { SampleAction, SampleLifecycle } from './types';
import { useTranslation } from 'react-i18next';
import { TFunction } from 'i18next';
import { getRowId } from './helper';
import { GeneratePaFromGroupsModal } from '../productApproval/GeneratePaFromGroupsModal';
import { AddSamplesToBrandTransportRequestModal } from '../transportRequests/AddSamplesToBrandTransportRequestModal';
import { AddSamplesToTransportInternalRequestModal } from '../transportRequests/AddSamplesToTransportInternalRequestModal';
import { transportRequestEvents } from '../transportRequests/events';
import { sampleEvents } from './events';
import { SetSamplesAsReceivedFromSupplierModal } from './SetSamplesAsReceivedFromSupplierModal';
import { SetSamplesReadyDateModal } from './SetSamplesReadyDateModal';
import React from 'react';
import { RemoveSamplesFromTransportRequestModal } from '../transportRequests/RemoveSamplesFromTransportRequestModal';
import {
  ProductApprovalState,
  SimpleProductApproval,
} from '@texas/api/endpoints/productApprovalsApi';
import { useSignalSubscriptionEvents } from '../hooks/useSignalSubscriptionEvents';

interface BaseSample {
  groupId: number;
  groupArchived: boolean;
  variantId: number;
  branchId: number;
  supplierId: number;
  articleFolderId: number;
  sample: {
    sampleId: number;
    sampleReadyDate: string | null;
    receivedFromSupplierUnknownShipment: string | null;
    transport: SampleTransportRequest | null;
    productApproval: SimpleProductApproval;
    state: SampleState;
    lifecycle: SampleLifecycle;
    inPossessionOfUserId?: number;
  } | null;
}

export function useMultiSelectSamples(hideStopFollowing?: boolean) {
  const { t } = useTranslation();
  const [selectedRows, setSelectedRows] = useState<RowSelectionState>({});
  const [samples, setSamples] = useState<BaseSample[]>([]);

  const initSamplingDisclosure = useValueDisclosure<number[]>();
  const receivedSamplesDisclosure = useValueDisclosure<number[]>();
  const editReadyDateDisclosure = useValueDisclosure<number[]>();
  const sendInternalDisclosure = useValueDisclosure<number[]>();
  const sendCustomerDisclosure = useValueDisclosure<number[]>();
  const removeFromTransportRequestDisclosure = useValueDisclosure<number[]>();
  const onPerformedActionCallback = useCallback(() => {
    sampleEvents.onSamplesActionPerformed.dispatch();
  }, []);
  const {
    setLostOnOpen,
    updateOwnerOnOpen,
    stopFollowOnOpen,
    rejectOnOpen,
    modals,
  } = useGeneralSampleModals({
    onPerformedAction: onPerformedActionCallback,
  });

  const filterBySamples = useCallback(
    (s: BaseSample[]) => {
      setSamples(s.filter((f) => selectedRows[getRowId(f.sample!.sampleId)]));
    },
    [selectedRows],
  );

  const filterByVersions = useCallback(
    (s: BaseSample[]) => {
      setSamples(s.filter((f) => selectedRows[getRowId(f.groupId)]));
    },
    [selectedRows],
  );

  useSignalSubscriptionEvents({
    triggerOnEvent: () => {
      setSelectedRows({});
    },
    signalEvents: [sampleEvents.onSamplesActionPerformed],
  });

  const menuItems = useMemo(() => {
    const uniqueStates: Record<string, SampleNextActionProps[]> = {};

    samples.forEach((x) => {
      const state = x.sample ? x.sample.lifecycle : SampleLifecycle.Nothing;

      const nextAction = evaluateSampleNextAction({
        state,
        sampleId: x.sample?.sampleId,
        versionArchived: x.groupArchived,
      });

      // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
      if (uniqueStates[nextAction] == undefined) {
        uniqueStates[nextAction] = [
          {
            action: nextAction,
            lifecycle: state,
            transportRequestId: x.sample?.transport?.id,
            pa: x.sample
              ? {
                  id: x.sample.productApproval.productApprovalId,
                  state: x.sample.productApproval.state,
                }
              : undefined,
            sampleId: x.sample?.sampleId,
            inPossessionOfUserId: x.sample?.inPossessionOfUserId,
            groupId: x.groupId,
            variantId: x.variantId,
            supplierId: x.supplierId,
            articleFolderId: x.articleFolderId,
            shipmentId: x.sample?.transport?.shipment?.id,
            branchId: x.branchId,
          },
        ];
        return;
      }

      uniqueStates[nextAction].push({
        action: nextAction,
        lifecycle: state,
        transportRequestId: x.sample?.transport?.id,
        pa: x.sample
          ? {
              id: x.sample.productApproval.productApprovalId,
              state: x.sample.productApproval.state,
            }
          : undefined,
        sampleId: x.sample?.sampleId,
        inPossessionOfUserId: x.sample?.inPossessionOfUserId,
        groupId: x.groupId,
        variantId: x.variantId,
        supplierId: x.supplierId,
        articleFolderId: x.articleFolderId,
        shipmentId: x.sample?.transport?.shipment?.id,
        branchId: x.branchId,
      });
    });

    return MultiSelectSamplesMenuActions({
      uniqueStates,
      onInititateSamplingClick: initSamplingDisclosure.onOpen,
      onReceivedSamplesClick: receivedSamplesDisclosure.onOpen,
      onEditReadyDateClick: editReadyDateDisclosure.onOpen,
      onSendInternalClick: sendInternalDisclosure.onOpen,
      onSendCustomerClick: sendCustomerDisclosure.onOpen,
      onRemoveTransportRequestClick:
        removeFromTransportRequestDisclosure.onOpen,
      updateOwnerOnOpen: updateOwnerOnOpen,
      setLostOnOpen: setLostOnOpen,
      stopFollowOnOpen: hideStopFollowing ? null : stopFollowOnOpen,
      rejectOnOpen: rejectOnOpen,
      t,
    });
  }, [
    hideStopFollowing,
    initSamplingDisclosure.onOpen,
    receivedSamplesDisclosure.onOpen,
    editReadyDateDisclosure.onOpen,
    rejectOnOpen,
    removeFromTransportRequestDisclosure.onOpen,
    samples,
    sendCustomerDisclosure.onOpen,
    sendInternalDisclosure.onOpen,
    setLostOnOpen,
    stopFollowOnOpen,
    t,
    updateOwnerOnOpen,
  ]);

  const modalComponents = useMemo(
    () => (
      <>
        {initSamplingDisclosure.value && (
          <GeneratePaFromGroupsModal
            groupIds={initSamplingDisclosure.value}
            isOpen={initSamplingDisclosure.isOpen}
            onClose={initSamplingDisclosure.onClose}
            onSubmit={() => {
              sampleEvents.onSamplesActionPerformed.dispatch();
            }}
          />
        )}

        {receivedSamplesDisclosure.value && (
          <SetSamplesAsReceivedFromSupplierModal
            isOpen={receivedSamplesDisclosure.isOpen}
            onClose={receivedSamplesDisclosure.onClose}
            sampleIds={receivedSamplesDisclosure.value}
            onReceived={() => {
              sampleEvents.onSampleReceivedFromSupplier.dispatch();
              sampleEvents.onSamplesActionPerformed.dispatch();
            }}
          />
        )}

        {editReadyDateDisclosure.value && (
          <SetSamplesReadyDateModal
            isOpen={editReadyDateDisclosure.isOpen}
            onClose={editReadyDateDisclosure.onClose}
            sampleIds={editReadyDateDisclosure.value}
            onPerformed={() => {
              sampleEvents.onSamplesActionPerformed.dispatch();
            }}
          />
        )}

        {sendInternalDisclosure.value && (
          <AddSamplesToTransportInternalRequestModal
            isOpen={sendInternalDisclosure.isOpen}
            onClose={sendInternalDisclosure.onClose}
            sampleIds={sendInternalDisclosure.value}
            onAdded={() => {
              transportRequestEvents.onSamplesAdded.dispatch();
              sampleEvents.onSamplesActionPerformed.dispatch();
            }}
          />
        )}

        {sendCustomerDisclosure.value && (
          <AddSamplesToBrandTransportRequestModal
            isOpen={sendCustomerDisclosure.isOpen}
            onClose={sendCustomerDisclosure.onClose}
            sampleIds={sendCustomerDisclosure.value}
            onAdded={() => {
              transportRequestEvents.onSamplesAdded.dispatch();
              sampleEvents.onSamplesActionPerformed.dispatch();
            }}
          />
        )}

        {removeFromTransportRequestDisclosure.value && (
          <RemoveSamplesFromTransportRequestModal
            isOpen={removeFromTransportRequestDisclosure.isOpen}
            onClose={removeFromTransportRequestDisclosure.onClose}
            sampleIds={removeFromTransportRequestDisclosure.value}
            onPerformed={() => {
              transportRequestEvents.onSamplesRemoved.dispatch();
              sampleEvents.onSamplesActionPerformed.dispatch();
            }}
          />
        )}

        {modals}
      </>
    ),
    [
      initSamplingDisclosure.isOpen,
      initSamplingDisclosure.onClose,
      initSamplingDisclosure.value,
      modals,
      receivedSamplesDisclosure.isOpen,
      receivedSamplesDisclosure.onClose,
      receivedSamplesDisclosure.value,
      editReadyDateDisclosure.isOpen,
      editReadyDateDisclosure.onClose,
      editReadyDateDisclosure.value,
      removeFromTransportRequestDisclosure.isOpen,
      removeFromTransportRequestDisclosure.onClose,
      removeFromTransportRequestDisclosure.value,
      sendCustomerDisclosure.isOpen,
      sendCustomerDisclosure.onClose,
      sendCustomerDisclosure.value,
      sendInternalDisclosure.isOpen,
      sendInternalDisclosure.onClose,
      sendInternalDisclosure.value,
    ],
  );

  return {
    filterBySamples,
    filterByVersions,
    menuItems,
    setSelectedRows,
    selectedRows,
    modalComponents,
  };
}

interface MultiSelectProps {
  uniqueStates: Record<string, SampleNextActionProps[]>;
  onInititateSamplingClick: (groupIds: number[]) => void;
  onReceivedSamplesClick: (sampleIds: number[]) => void;
  onEditReadyDateClick: (sampleIds: number[]) => void;
  onSendInternalClick: (sampleIds: number[]) => void;
  onSendCustomerClick: (sampleIds: number[]) => void;
  onRemoveTransportRequestClick: (sampleIds: number[]) => void;
  updateOwnerOnOpen: (sampleIds: number[]) => void;
  setLostOnOpen: (sampleIds: number[]) => void;
  stopFollowOnOpen: null | ((sampleIds: number[]) => void);
  rejectOnOpen: (id: number) => void;
  t: TFunction;
}

export function MultiSelectSamplesMenuActions(props: MultiSelectProps) {
  const components: React.ReactNode[] = [];
  const sampleIds: number[] = [];
  const pas: { id: number; state: ProductApprovalState }[] = [];
  let noOfActions = 0;

  Object.entries(props.uniqueStates).map(([k, samples]) => {
    const action = ensureEnumNumber(
      SampleAction,
      parseInt(k),
    ) as unknown as SampleAction;

    const actionSampleIds = samples
      .filter((s) => s.sampleId !== undefined)
      .map((s) => s.sampleId!);
    const actionPas = samples
      .filter((s) => s.pa !== undefined)
      .map((x) => ({
        id: x.pa!.id,
        state: x.pa!.state,
      }));

    if (
      action === SampleAction.InitiateSampling ||
      action === SampleAction.RestartSampling
    ) {
      components.push(
        <React.Fragment key={action}>
          <MenuItem
            icon={<Icons.Gift boxSize={defaultIconSize} />}
            onClick={() =>
              props.onInititateSamplingClick(samples.map((x) => x.groupId))
            }
          >
            <Flex gap={1} alignItems="center">
              <Tag>{samples.length}</Tag>
              <Text>{props.t('sample.initate')}</Text>
            </Flex>
          </MenuItem>
          <MenuDivider />
        </React.Fragment>,
      );
      noOfActions++;
    }

    if (action === SampleAction.MarkSampleReceivedFromSupplier) {
      components.push(
        <React.Fragment key={action}>
          <MenuItem
            icon={<Icons.GiftOpen boxSize={defaultIconSize} />}
            onClick={() => props.onReceivedSamplesClick(actionSampleIds)}
          >
            <Flex gap={1} alignItems="center">
              <Tag>{samples.length}</Tag>
              <Text>{props.t('sample.setReceivedFromSupplier')}</Text>
            </Flex>
          </MenuItem>
          <MenuItem
            icon={<Icons.CalendarCheck boxSize={defaultIconSize} />}
            onClick={() => props.onEditReadyDateClick(actionSampleIds)}
          >
            <Flex gap={1} alignItems="center">
              <Tag>{samples.length}</Tag>
              <Text>{props.t('sample.editReadyDate')}</Text>
            </Flex>
          </MenuItem>
          <MenuDivider />
        </React.Fragment>,
      );
      noOfActions++;
    }

    if (
      action === SampleAction.SendToInternalOrHandlePa ||
      action === SampleAction.SendSampleOrHandlePa
    ) {
      components.push(
        <React.Fragment key={action}>
          <MenuItem
            onClick={() => props.onSendCustomerClick(actionSampleIds)}
            icon={<Icons.TruckFast boxSize={defaultIconSize} />}
          >
            <Flex gap={1} alignItems="center">
              <Tag>{samples.length}</Tag>
              <Text>{props.t('sample.sendCustomer')}</Text>
            </Flex>
          </MenuItem>
          <MenuItem
            onClick={() => props.onSendInternalClick(actionSampleIds)}
            icon={<Icons.TruckDelivery boxSize={defaultIconSize} />}
          >
            <Flex gap={1} alignItems="center">
              <Tag>{samples.length}</Tag>
              <Text>{props.t('sample.sendInternal')}</Text>
            </Flex>
          </MenuItem>
          <MenuDivider />
        </React.Fragment>,
      );
      noOfActions++;
    }

    if (action === SampleAction.UpdateTransportRequestWithoutShipment) {
      components.push(
        <React.Fragment key={action}>
          <MenuItem
            onClick={() => props.onRemoveTransportRequestClick(actionSampleIds)}
            icon={<Icons.CloseCircle boxSize={defaultIconSize} />}
          >
            <Flex gap={1} alignItems="center">
              <Tag>{samples.length}</Tag>
              <Text>{props.t('sample.removeFromTransportRequest')}</Text>
            </Flex>
          </MenuItem>
          <MenuDivider />
        </React.Fragment>,
      );
      noOfActions++;
    }

    pas.push(...actionPas);
    sampleIds.push(...actionSampleIds);
  });

  if (pas.length === 0 && sampleIds.length === 0 && noOfActions === 0)
    return null;

  components.push(
    <GeneralSamplesMenuActions
      key={SampleAction.None}
      pas={pas}
      sampleIds={sampleIds}
      rejectOnOpen={props.rejectOnOpen}
      setLostOnOpen={props.setLostOnOpen}
      stopFollowOnOpen={props.stopFollowOnOpen}
      updateOwnerOnOpen={props.updateOwnerOnOpen}
      onPerformedAction={() => sampleEvents.onSamplesActionPerformed.dispatch()}
    />,
  );

  return components;
}
